/**
 * THE GUARANTEED INCOME SECTION OF THE ADVICE DOCUMENT (owner, 21 Oct 2026 — the guaranteed income review R1–R4
 * and the agreed mock-up, docs/ux-review/annuities/ screen 5).
 *
 * For a plan with a guaranteed income for life, the document says:
 *   - what was recommended, in the words for THAT type (what the family receives), with the income and where
 *     the figure came from: our estimate from the published rates and their date (and whether they are more
 *     than two weeks old, annuityRateSheet), or the provider's quote; a written quote is needed before buying;
 *   - the year-by-year table of what the family receives if the person passes away (the same table as the
 *     plan card, AnnuityFamilyPanel), against the same plan without the guaranteed income where there is one;
 *   - the alternatives: the best plan tested without a guaranteed income;
 *   - the assumptions, in plain words, including that the figures assume the person lives the whole period;
 *   - the client's own answers to the questions, with the date;
 *   - for income only, the client's confirmation word for word, with its date.
 *
 * Plain data, frozen when the document is made (the snapshot is re-rendered later by the licensee view). No
 * product or provider is named. Used by the village, downsizing and granny flat documents (moduleSoa.ts).
 */
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { formatCurrency } from '../../utils/index.ts';
import { estateOf } from '../../../../shared/src/calculation/support/estateCgt.ts';
import { annuityRateSheet } from '../../../../shared/src/calculation/annuityRates.ts';
import { annuityPreferenceOf, annuityCeilingOf } from '../../../../shared/src/calculation/annuityPreference.ts';
import { annuityDeathValueAt, familyPanelYears, type FamilyPanelKind } from '../../components/strategy/AnnuityFamilyPanel.tsx';
import { incomeOnlyRecordOf, incomeOnlyConfirmedFor } from '../../components/strategy/IncomeOnlyTick.tsx';

export interface SoaGuaranteedIncomeRow {
    /** "In the first year", "After 5 years". */
    label: string;
    /** What the guaranteed income itself pays the family on death that year. */
    fromAnnuity: number;
    /** What the family receives in total with this plan. */
    withPlan: number | null;
    /** The same plan without the guaranteed income, where there is one. */
    without: number | null;
}

export interface SoaGuaranteedIncome {
    personName: string;
    amount: number;
    kind: FamilyPanelKind;
    incomeAYear: number | null;
    /** True when the income is the provider's quote rather than our estimate. */
    quoted: boolean;
    /** The date the published rates took effect (YYYY-MM-DD), and whether they are more than two weeks old. */
    rateSheetDate: string | null;
    rateSheetStale: boolean;
    rows: SoaGuaranteedIncomeRow[];
    compareNote: string | null;
    /** The best plan tested without a guaranteed income. */
    alternative: { name: string; estate: number; pension: number; cashflow: number } | null;
    /** The plan's own figures, for the comparison with the alternative. */
    plan: { estate: number; pension: number; cashflow: number };
    horizonYears: number;
    /** The client's answers to the guaranteed-income questions, in words, with the date. */
    answers: { answer: string; ceiling: string; answeredOn: string | null };
    /** Income only: the client's confirmation, word for word. */
    incomeOnlyRecord: { amount: number; date: string; words: string } | null;
}

type Row = Record<string, unknown>;

export interface SoaGuaranteedIncomeInput {
    factFind: DraftFactFind | null | undefined;
    personName: string;
    amount: number;
    kind: FamilyPanelKind;
    incomeAYear?: number | null;
    quoted?: boolean;
    /** The plan's own engine rows. */
    rows: readonly unknown[] | null | undefined;
    /** The same plan without the guaranteed income. */
    compareRows?: readonly unknown[] | null;
    compareNote?: string | null;
    horizonYears: number;
    plan: { estate: number; pension: number; cashflow: number };
    alternative?: { name: string; estate: number; pension: number; cashflow: number } | null;
}

/** The client's answers in the words of the question (screen 1 of the mock-up). */
export function guaranteedIncomeAnswerWords(factFind: DraftFactFind | null | undefined): { answer: string; ceiling: string; answeredOn: string | null } {
    const pref = annuityPreferenceOf(factFind as never);
    const ceiling = annuityCeilingOf(factFind as never, (factFind?.goals as Record<string, unknown> | undefined)?.rvAnnuityShareCapPct as number | undefined);
    const answer = !pref.answered
        ? 'Not answered. We tested the types that pay the money back to the family (the suggested answer).'
        : pref.appetite === 'none'
            ? '"No, keep all the money within reach."'
            : pref.residual === 'all' || pref.residual === 'nil'
                ? '"Yes, and the most income, even if nothing goes back."'
                : '"Yes, if the money goes back to the family."';
    const ceilingWords = !ceiling.answered
        ? 'Not answered. We used the suggested ceiling: no more than 25% of the money that could be invested.'
        : ceiling.amount != null
            ? `No more than ${formatCurrency(Math.round(ceiling.amount))}.`
            : `No more than ${ceiling.pct}% of the money that could be invested.`;
    return { answer, ceiling: ceilingWords, answeredOn: ceiling.answeredOn };
}

export function buildSoaGuaranteedIncome(i: SoaGuaranteedIncomeInput): SoaGuaranteedIncome | null {
    const list = (i.rows || []) as Row[];
    if (!(i.amount > 0) || !list.length) return null;
    const compare = (i.compareRows || []) as Row[];
    const years = familyPanelYears(i.horizonYears, list.length);
    const rows = years.map((y) => ({
        label: y === 1 ? 'In the first year' : `After ${y} years`,
        fromAnnuity: Math.round(annuityDeathValueAt(list[y - 1] as never, i.factFind)),
        withPlan: list[y - 1] ? Math.round(Number(estateOf(list[y - 1] as never)) || 0) : null,
        without: compare.length ? Math.round(Number(estateOf(compare[Math.min(y, compare.length) - 1] as never)) || 0) : null,
    }));
    let sheetDate: string | null = null;
    let stale = false;
    try { const s = annuityRateSheet(); sheetDate = s.effectiveFrom || null; stale = !!s.stale; } catch { /* no sheet: said as an estimate */ }
    const record = i.kind === 'incomeOnly' && incomeOnlyConfirmedFor(i.factFind, i.amount) ? incomeOnlyRecordOf(i.factFind) : null;
    return {
        personName: i.personName,
        amount: Math.round(i.amount),
        kind: i.kind,
        incomeAYear: i.incomeAYear != null && i.incomeAYear > 0 ? Math.round(i.incomeAYear) : null,
        quoted: !!i.quoted,
        rateSheetDate: sheetDate,
        rateSheetStale: stale,
        rows,
        compareNote: compare.length ? (i.compareNote || null) : null,
        alternative: i.alternative ?? null,
        plan: i.plan,
        horizonYears: Math.max(1, Math.round(i.horizonYears) || 1),
        answers: guaranteedIncomeAnswerWords(i.factFind),
        incomeOnlyRecord: record ? { amount: Math.round(Number(record.amount) || 0), date: String(record.date || ''), words: String(record.words || '') } : null,
    };
}

const esc = (v: unknown): string => String(v ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
const money = (n: number): string => formatCurrency(Math.round(n));
const dateWords = (iso: string | null): string => {
    if (!iso) return '';
    const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
};

/** What the recommendation buys, in the words for its type (what the family receives). */
export function guaranteedIncomeTypeWords(kind: FamilyPanelKind, name: string): string {
    if (kind === 'fullBack') return `a lifetime guaranteed income with life cover, which returns the full amount to ${name}’s estate whenever ${name} passes away`;
    if (kind === 'moneyBack') return `a lifetime guaranteed income that returns the money to ${name}’s estate in the early years: the whole amount if ${name} passes away in about the first half of ${name}’s life expectancy, then a smaller amount, falling to nothing`;
    return `a lifetime guaranteed income that pays the most income and returns nothing to ${name}’s estate, even if ${name} passes away soon after buying it`;
}

/** The section's body, for the shared document (moduleSoa.ts). */
export function renderGuaranteedIncomeSectionHtml(g: SoaGuaranteedIncome): string {
    const name = esc(g.personName);
    const withCompare = g.rows.some((r) => r.without != null);
    const source = g.quoted
        ? 'the provider’s quote'
        : `an estimate from published rates${g.rateSheetDate ? ` dated ${esc(dateWords(g.rateSheetDate))}` : ''}${g.rateSheetStale ? ' (those rates are more than two weeks old, so the income may have changed)' : ''}`;
    const income = g.incomeAYear ? ` The income is about ${money(g.incomeAYear)} a year, ${source}.` : ` The income is ${source}.`;
    const table = `<div style="overflow-x:auto"><table><thead><tr><th>If ${name} passes away</th><th style="text-align:right">From the guaranteed income</th><th style="text-align:right">The family receives in total, with this plan</th>${withCompare ? '<th style="text-align:right">The same plan without a guaranteed income</th>' : ''}</tr></thead><tbody>
${g.rows.map((r) => `<tr><td>${esc(r.label)}</td><td class="td-num" style="text-align:right">${r.fromAnnuity < 1 ? 'nothing' : money(r.fromAnnuity)}</td><td class="td-num" style="text-align:right">${r.withPlan == null ? '—' : money(r.withPlan)}</td>${withCompare ? `<td class="td-num" style="text-align:right">${r.without == null ? '—' : money(r.without)}</td>` : ''}</tr>`).join('')}
</tbody></table></div>${withCompare && g.compareNote ? `<p class="small muted">The last column is ${esc(g.compareNote)}, so the difference is the guaranteed income alone.</p>` : ''}`;
    const alt = g.alternative;
    const altLine = alt
        ? `<p><strong>Why this and not the alternatives.</strong> We also tested plans without a guaranteed income. The best of them, ${esc(alt.name)}, leaves ${money(alt.estate)} to the family after ${g.horizonYears} years, against ${money(g.plan.estate)} with this plan; its Age Pension is ${money(alt.pension)} a year against ${money(g.plan.pension)}, and it leaves ${money(alt.cashflow)} a year to spare against ${money(g.plan.cashflow)}. ${g.kind === 'incomeOnly' ? 'This plan pays more while ' + name + ' lives, and less to the family if ' + name + ' passes away early.' : 'The table above shows what the family receives at each point if ' + name + ' passes away sooner.'}</p>`
        : '<p><strong>Why this and not the alternatives.</strong> The other plans tested, with and without a guaranteed income, are compared in “Alternative strategies considered”.</p>';
    const centrelink = g.kind === 'fullBack'
        ? 'Centrelink counts the income part at 60% and the life cover in full.'
        : 'Centrelink counts 60% of the price for the first five years, or until age 85 if that is later, and 30% after that, and 60% of each payment as income.';
    const assumptions = `<p><strong>Assumptions.</strong> The figures after ${g.horizonYears} years assume ${name} lives the whole period; the table shows what the family receives if ${name} passes away sooner. ${centrelink} The income is ${g.quoted ? 'the provider’s quote, scaled to this amount' : 'our estimate, not a quote'}. Before buying, ${name} should obtain a written quote; the provider’s quote replaces our estimate. Money in a guaranteed income is hard to get back out once it is paid.</p>`;
    const answers = `<p><strong>${name}’s answers${g.answers.answeredOn ? `, given on ${esc(dateWords(g.answers.answeredOn))}` : ''}.</strong> Would ${name} consider putting part of the money into a guaranteed income for life? ${esc(g.answers.answer)} At most, how much could go into it? ${esc(g.answers.ceiling)}</p>`;
    const record = g.kind !== 'incomeOnly'
        ? ''
        : g.incomeOnlyRecord
            ? `<div class="callout" style="border:1px solid #cfd6e0;border-left:4px solid #d97706;background:#fff"><p class="small" style="margin:0">On ${esc(dateWords(g.incomeOnlyRecord.date))}, ${name} confirmed: “${esc(g.incomeOnlyRecord.words)}”</p></div>`
            : `<div class="callout" style="border:1px solid #cfd6e0;border-left:4px solid #d97706;background:#fff"><p class="small" style="margin:0">No confirmation for ${money(g.amount)} is on file. Nothing from it comes back to ${name} or the family, even if ${name} passes away soon after buying it; ${name} must confirm this before going ahead.</p></div>`;
    return `<p class="lede">We recommend ${name} puts ${money(g.amount)} into ${esc(guaranteedIncomeTypeWords(g.kind, g.personName))}.${income}</p>
${table}
${altLine}
${assumptions}
${answers}
${record}`;
}
